import React, { useState } from "react";
import { Loader2, ShieldCheck, Copy, ExternalLink, Wrench, CheckCircle2, AlertTriangle, MinusCircle } from "lucide-react";
import { toast } from "./ui/sonner";

export const LINK_KIND = {
  owner_portal: ["业主门户", "Owner portal"],
  weekly: ["业主周报", "Weekly recap"],
  photo_wall: ["照片墙链接", "Photo wall"],
};
export const LINK_STATUS = {
  ok: ["可正常打开", "Opens OK", "ok"],
  missing: ["链接缺失", "Missing", "bad"],
  none: ["尚未生成", "Not created", "muted"],
  expired: ["已过期", "Expired", "bad"],
  revoked: ["已人工撤销（不自动恢复）", "Revoked manually", "muted"],
  http_fail: ["打不开（接口异常）", "Fails to open", "bad"],
};
const TONE = { ok: "bg-emerald-50 text-emerald-700 border-emerald-200", bad: "bg-rose-50 text-rose-700 border-rose-200", muted: "bg-slate-100 text-slate-600 border-slate-200" };

export const LinkStatusBadge = ({ status, zh }) => {
  const [z, e, tone] = LINK_STATUS[status] || [status, status, "muted"];
  const Icon = tone === "ok" ? CheckCircle2 : tone === "bad" ? AlertTriangle : MinusCircle;
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-px text-[11px] font-semibold ${TONE[tone]}`} data-status={status}><Icon className="h-3 w-3" />{zh ? z : e}</span>;
};

const copy = async (t, zh) => { try { await navigator.clipboard.writeText(t); toast.success(zh ? "已复制" : "Copied"); } catch { toast.error(zh ? "复制失败" : "Copy failed"); } };

/* 单项目：检测 业主门户 / 周报 / 照片墙 链接是否有效 → 一键修复（沿用原链接续期 / 补生成） */
export const OwnerLinkCheck = ({ api, projectId, zh, onFixed }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  const run = async () => {
    setBusy("check");
    try { const { data } = await api.get(`/projects/${projectId}/owner-links/check`); setRes(data); } catch (e) { toast.error(e?.detailText || (zh ? "检测失败" : "Check failed")); } finally { setBusy(""); }
  };
  const fix = async (kinds) => {
    setBusy("fix");
    try {
      const { data } = await api.post(`/projects/${projectId}/owner-links/fix`, Array.isArray(kinds) ? { kinds } : {});
      setRes(data);
      toast.success(data.fixed?.length ? (zh ? `已修复 ${data.fixed.length} 项` : `Fixed ${data.fixed.length}`) : (zh ? "没有可自动修复的项" : "Nothing to fix"));
      if (data.fixed?.length) onFixed?.(data);
    } catch (e) { toast.error(e?.detailText || (zh ? "修复失败" : "Fix failed")); } finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="owner-link-check" data-ok={res ? (res.allOk ? 1 : 0) : ""}>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" data-testid="owner-link-check-btn" disabled={!!busy} onClick={run} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full border border-indigo-200 bg-white text-indigo-700 px-3 py-1 hover:bg-indigo-50 disabled:opacity-50">
          {busy === "check" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}{zh ? "检测链接是否有效" : "Check links"}
        </button>
        {res && res.fixable > 0 && <button type="button" data-testid="owner-link-fix-btn" disabled={!!busy} onClick={() => fix()} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-brand-grad text-white px-3 py-1 hover:opacity-90 disabled:opacity-50">
          {busy === "fix" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wrench className="h-3.5 w-3.5" />}{zh ? `自动修复 ${res.fixable} 项` : `Auto-fix ${res.fixable}`}
        </button>}
        {res && <span className={`text-[11px] font-semibold ${res.allOk ? "text-emerald-700" : "text-rose-700"}`} data-testid="owner-link-check-summary">{res.allOk ? (zh ? "全部可正常打开" : "All links OK") : (zh ? `${res.problems} 项需要处理` : `${res.problems} issue(s)`)}</span>}
      </div>
      {res && <ul className="space-y-1.5" data-testid="owner-link-check-result">
        {res.items.map((it) => (
          <li key={it.kind} data-testid={`owner-link-item-${it.kind}`} data-status={it.status} className="flex items-center gap-2 text-xs">
            <span className="w-20 shrink-0 font-semibold text-slate-700">{zh ? LINK_KIND[it.kind][0] : LINK_KIND[it.kind][1]}</span>
            <LinkStatusBadge status={it.status} zh={zh} />
            {it.kind === "photo_wall" && it.expiresAt && <span className="text-[11px] text-slate-500">{zh ? "到期" : "Expires"} {String(it.expiresAt).slice(0, 10)}</span>}
            {it.kind === "weekly" && it.weekStart && <span className="text-[11px] text-slate-500">{it.weekStart} ~ {it.weekEnd}</span>}
            {it.kind === "owner_portal" && it.status === "none" && <button type="button" data-testid="owner-link-gen-portal" disabled={!!busy} onClick={() => fix(["owner_portal"])} className="text-[11px] font-semibold text-indigo-700 hover:underline disabled:opacity-50">{zh ? "立即生成" : "Generate now"}</button>}
            {it.url && it.ok && <span className="ml-auto inline-flex items-center gap-1">
              <button type="button" data-testid={`owner-link-copy-${it.kind}`} onClick={() => copy(it.url, zh)} className="p-1 rounded-md text-slate-500 hover:bg-slate-200/70" title={zh ? "复制链接" : "Copy"}><Copy className="h-3.5 w-3.5" /></button>
              <a href={it.url} target="_blank" rel="noreferrer" data-testid={`owner-link-open-${it.kind}`} className="p-1 rounded-md text-slate-500 hover:bg-slate-200/70" title={zh ? "打开" : "Open"}><ExternalLink className="h-3.5 w-3.5" /></a>
            </span>}
          </li>
        ))}
      </ul>}
      {res?.fixed?.length > 0 && <p className="text-[11px] text-emerald-700" data-testid="owner-link-fixed">{zh ? "已修复：" : "Fixed: "}{res.fixed.map((f) => `${zh ? LINK_KIND[f.kind][0] : LINK_KIND[f.kind][1]}${f.action === "renewed" ? (zh ? `（沿用原链接续期 ${f.days} 天）` : ` (renewed ${f.days}d)`) : f.action === "generated" ? (zh ? "（已生成）" : " (generated)") : (zh ? "（已补链接）" : " (token added)")}`).join(" · ")}</p>}
    </div>
  );
};
